import {
  Badge,
  BadgeProps,
  HStack,
  Text,
  TextProps,
  Tooltip,
} from "@chakra-ui/react";
import {
  MarketingAdSet,
  MarketingCampaign,
} from "common/interfaces/KYDMarketing";

type StatusBadgeStatus = MarketingCampaign["status"] | MarketingAdSet["status"];

export const colorForStatus = (status: StatusBadgeStatus) => {
  if (status === "ACTIVE") return "blue.100";
  if (status === "PAUSED") return "yellow.100";
  if (status === "ARCHIVED") return "gray.400";
  if (status === "TO_DELETE") return "gray.100";
  if (status === "DELETED") return "gray.100";
  if (status === "FROZEN") return "gray.100";
  if (status === "DRAFT") return "gray.100";
  return "gray.400";
};

export const getTextForStatus = (status: StatusBadgeStatus | string) => {
  if (!status) return "";
  if (status === "FROZEN")
    return "KYD freezes these objects when they were changed from the ads manager side and use features not supported by KYD ads manager";
  return status.toLowerCase();
};

const badgeStyles: BadgeProps = {
  borderRadius: "0.625rem",
  bg: "#AEDFAA",
  display: "flex",
  px: 2,
  justifyContent: "center",
  height: 4,
  alignItems: "center",
  gap: "0.625rem",
  alignSelf: "stretch",
  cursor: "text",
  userSelect: "text" as const,
  _hover: { opacity: 0.8 },
};

const textStyles: TextProps = {
  color: "#1F531A",
  fontFamily: "Work Sans",
  fontSize: "xx-small",
  fontWeight: 500,
  lineHeight: "100%",
};

const colorSchemeForStatus = (status: StatusBadgeStatus) => {
  if (status === "ACTIVE") return "green.100";
  if (status === "PAUSED") return "yellow.100";
  if (status === "ARCHIVED") return "gray.400";
  if (status === "TO_DELETE") return "gray.100";
  if (status === "DELETED") return "gray.100";
  if (status === "DRAFT") return "gray.100";
};

export const StatusBadge = ({ status }: { status: StatusBadgeStatus }) => {
  /** Active is the default "healthy" state — no badge (same as performance "On Track"). */
  if (status === "ACTIVE") return null;

  const statusText = getTextForStatus(status);

  const handleTextSelect = (e: React.MouseEvent) => {
    e.preventDefault();
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(e.currentTarget);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const label = status?.toUpperCase() ?? "";

  const badge = (
    <Badge
      {...badgeStyles}
      colorScheme={colorSchemeForStatus(status)}
      bg={colorForStatus(status)}
    >
      <HStack gap="0.625rem">
        <Text
          {...textStyles}
          textTransform="capitalize"
          onDoubleClick={handleTextSelect}
        >
          {label}
        </Text>
      </HStack>
    </Badge>
  );

  return (
    <Tooltip
      label={`${status}:\n${statusText}`}
      hasArrow
      placement="top"
      bg="gray.700"
      color="white"
      fontSize="sm"
    >
      {badge}
    </Tooltip>
  );
};
